<template>
    <div class="space-y-1">
        <!-- With Attachments -->
        <div v-if="attachments.length" class="flex flex-wrap items-center gap-2">
            <div class="flex items-center space-x-2">
                <div class="flex items-center">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span class="ml-1 text-xs text-gray-500">{{ attachments.length }}</span>
                </div>
                <button @click="showUpload = true"
                        v-if="!showUpload"
                        class="p-1 text-gray-400 hover:text-indigo-600 transition-colors duration-150"
                        title="Add attachment">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                </button>
            </div>
            
            <div class="flex flex-wrap gap-2">
                <div v-for="(doc, docI) in attachments" 
                     :key="doc.id"
                     class="group relative inline-flex items-center px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-150">
                    <a :href="`/assignments/${assignment.id}/download/${doc.id}`"
                       @click="visited(doc.id)"
                       :title="doc.original"
                       class="truncate max-w-[150px]">
                        {{ doc.original }}
                    </a>
                    <button @click="removeAttachment(doc.id, docI)"
                            class="ml-1.5 p-0.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            title="Remove attachment">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- No Attachments -->
        <div v-else-if="!showUpload" class="flex items-center justify-start text-xs text-gray-400">
            <div class="flex items-center">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                No attachments
            </div>
            <button @click="showUpload = true"
                    class="p-1 text-gray-400 hover:text-indigo-600 transition-colors duration-150"
                    title="Add attachment">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
            </button>
        </div>

        <!-- Upload Area -->
        <div v-if="showUpload" 
             class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50"
             @click.self="showUpload = false">
            <div class="relative w-full max-w-lg mx-4 bg-white rounded-lg shadow-xl">
                <div class="p-6">
                    <div class="relative">
                        <div class="absolute inset-0">
                            <file-upload 
                                name="attachments"
                                class="w-full"
                                @loaded="handleFileUpload">
                            </file-upload>
                        </div>
                        <div class="w-full p-4 border-2 border-dashed rounded-lg transition-colors duration-200 border-gray-300 bg-gray-50 hover:border-gray-400">
                            <div class="flex flex-col items-center justify-center space-y-2">
                                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <div class="text-center">
                                    <p class="text-sm text-gray-600">
                                        <span class="font-medium text-indigo-600 hover:text-indigo-500">
                                            Click to upload
                                        </span>
                                        or drag and drop
                                    </p>
                                    <p class="mt-1 text-xs text-gray-500">
                                        Any file type accepted
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-4 flex justify-end">
                        <button @click="showUpload = false"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import FileUpload from './FileUpload.vue';

    export default {
        components: { FileUpload },
        props: ['assignment'],
        data() {
            return {
                attachments: this.assignment.attachments,
                showUpload: false
            }
        },
        methods: {
            visited(id) {
                let current_url = window.location.href;
                history.replaceState({}, "", `/assignments/${this.assignment.id}/download/${id}`);
                history.replaceState({}, "", current_url);
            },
            removeAttachment(id, docI) {
                if (confirm('Are you sure you want to remove this attachment?')) {
                    axios.delete(`/attachments/${id}`)
                        .then(({ data }) => {
                            this.attachments.splice(docI, 1);
                        })
                        .catch(error => {
                            this.$toast?.error('Failed to remove attachment') || alert('Failed to remove attachment');
                        });
                }
            },
            handleFileUpload(doc) {
                let data = new FormData();
                data.append("attachment", doc.file);
                                
                axios.post(`/assignments/${this.assignment.id}/attachments`, data)
                    .then(({ data }) => {
                        this.attachments.push(data);
                        this.$toast?.success(`${doc.file.name} uploaded successfully`);
                        this.showUpload = false;
                    })
                    .catch(error => {
                        this.$toast?.error(`Failed to upload ${doc.file.name}`);
                    });
            }
        }
    }
</script> 